<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catAmber" class="mr-2 flex-shrink-0">
        {{ icon || "mdi-glass-wine" }}
      </v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <v-chip
        v-if="latest && latest.frequency"
        x-small
        :color="freqColor(latest.frequency)"
        text-color="white"
        class="ml-2 flex-shrink-0"
      >
        {{ freqLabel(latest.frequency) }}
      </v-chip>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on" class="flex-shrink-0 ml-1" @click="openDialog">
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar consumo de álcool</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <div v-if="!latest" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-glass-cocktail-off</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhum registro de consumo de álcool.
        </p>
        <v-btn small color="catAmber" outlined class="mt-3" @click="openQuickAdd">
          <v-icon small left>mdi-plus</v-icon>
          Registrar álcool
        </v-btn>
      </div>

      <div v-else class="info-list">
        <div class="info-row">
          <span class="info-label">Frequência</span>
          <span class="info-value">{{ freqLabel(latest.frequency) || "—" }}</span>
        </div>
        <div v-if="latest.notes" class="info-row">
          <span class="info-label">Observação</span>
          <span class="info-value">{{ latest.notes }}</span>
        </div>
      </div>
    </v-card-text>

    <EditAlcoholDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :records="records"
      :patient-id="patientId"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getAlcoholByPatient } from "@/services/alcoholService";
import EditAlcoholDialog from "@/features/patients/profile-dashboard/dialogs/EditAlcoholDialog.vue";

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";

import dashboardStore from "@/features/patients/profile-dashboard/patientDashboardStore";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "AlcoholCard",

  components: {
    EditAlcoholDialog,
  },

  props: {
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
  },

  data() {
    return { dialogVisible: false, records: [] };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.records || []).length;
    },
    latest() {
      return this.records.length ? this.records[0] : null;
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.records = await dashboardStore.section(this.patientId, "alcohol", () => getAlcoholByPatient(this.patientId));
      } catch (error) {
        console.error("Erro ao buscar consumo de álcool:", error);
        this.records = [];
      }
    },

    freqLabel(f) {
      return (
        {
          nao_bebo: "Não bebo",
          socialmente: "Socialmente",
          frequentemente: "Frequentemente",
        }[f] || ""
      );
    },

    freqColor(f) {
      return (
        {
          nao_bebo: "green darken-1",
          socialmente: "amber darken-2",
          frequentemente: "deep-orange darken-1",
        }[f] || "grey"
      );
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Âmbar (Álcool) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid #fde68a !important;
  box-shadow: 0 2px 10px rgba(180, 83, 9, 0.1) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #fcd34d !important;
  box-shadow: 0 6px 20px rgba(180, 83, 9, 0.18) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: var(--ds-soft-amber);
  border-bottom: 1px solid #fde68a;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-cat-amber);
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  border-bottom: 1px dashed #fef3c7;
  padding-bottom: 8px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-cat-amber);
  flex-shrink: 0;
}

.info-value {
  font-size: 13px;
  color: var(--ds-text-600);
  text-align: right;
}
</style>
